/**
 * The microsite read its facts from its own config. Here MP has them, so the
 * config is overlaid with the event's: what staff can change in October
 * without a developer (dates, registration close, venue) comes from the
 * details payload, and everything else stays content.ts.
 *
 * Times stay MP wall clock, no offset (docs/timezone-architecture.md); the
 * countdown compares them against the clock's wall "now".
 */
import type { EventDetailsResponse } from '@/lib/types';
import type { Stage } from '@/lib/stages';
import type { ConferenceConfig, ConferencePhase } from './conference';
import { whatMatters } from '../content';

const WEEKDAY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

function daysBetween(start: string, end: string | null): ConferenceConfig['days'] {
  const first = start.slice(0, 10);
  const last = (end ?? start).slice(0, 10);
  const out: ConferenceConfig['days'] = [];
  for (let d = new Date(`${first}T12:00:00`); out.length < 14; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const key = WEEKDAY[d.getDay()]!;
    out.push({ key, label: key[0]!.toUpperCase() + key.slice(1), date: iso });
    if (iso >= last) break;
  }
  return out;
}

export function facts(details: EventDetailsResponse): ConferenceConfig {
  const e = details.event;
  const loc = e.location;
  const cityLine = [loc.city, loc.state].filter(Boolean).join(', ');
  return {
    ...whatMatters,
    eventId: e.id,
    days: daysBetween(e.start, e.end),
    // No end date in MP means registration stays open until the event starts;
    // the microsite's own close date (Oct 11) is long past and would read
    // "Registration is closed" while it is open.
    registrationCloses: (e.registrationEnd ?? e.start).slice(0, 19),
    eventStarts: e.start,
    eventEnds: (e.end ?? whatMatters.eventEnds).slice(0, 19),
    venue: loc.line1
      ? {
          name: loc.name ?? whatMatters.venue.name,
          address: [loc.line1, loc.line2].filter(Boolean).join(', '),
          city: cityLine,
          mapUrl: whatMatters.venue.mapUrl,
          lat: loc.lat ?? undefined,
          lng: loc.lng ?? undefined,
        }
      : whatMatters.venue,
  };
}

/** The widget's stages, in the microsite's words. Announce shows as preEvent. */
export function phaseOf(stage: Stage): ConferencePhase {
  switch (stage) {
    case 'registration':
      return 'registration';
    case 'live':
      return 'live';
    case 'recap':
      return 'recap';
    case 'teaser':
      return 'teaser';
    default:
      return 'preEvent';
  }
}
